import React from 'react'
import ReactMarkdown from 'react-markdown'
import { ChatMessage, ToolCall } from '../../../services/apiService'

interface ToolCallPreviewProps {
  toolCall: ToolCall
  messageIndex: number
  isStreaming: boolean
  isLastMessage: boolean
  isExecuted: boolean
  isEditorLoading: boolean
  brandAssets: BrandAsset[]
  onExecute: (toolCall: ToolCall, messageIndex: number) => void
}

const ToolCallPreview: React.FC<ToolCallPreviewProps> = ({
  toolCall, messageIndex, isStreaming, isLastMessage, isExecuted, isEditorLoading, brandAssets, onExecute,
}) => {
  if (isStreaming && isLastMessage) return null

  const { name: opName, params } = toolCall

  // Determine which asset types are relevant based on operation
  const creativeTypes: BrandAssetType[] = ['logo', 'mannequin', 'hero_ad', 'lighting_ref', 'product_shot_ref']
  const layoutTypes: BrandAssetType[] = ['template']
  const relevantTypes: BrandAssetType[] =
    opName === 'reformat' ? layoutTypes
    : opName === 'inpaint' || opName === 'regional_edit' || opName === 'extend' ? creativeTypes
    : [...creativeTypes, ...layoutTypes]

  const excludedAssets = Array.isArray(params.excluded_asset_ids) && params.excluded_asset_ids.length > 0
    ? (params.excluded_asset_ids as string[])
        .map((id) => brandAssets.find((a) => a.id === id))
        .filter((a): a is BrandAsset => Boolean(a) && relevantTypes.includes(a!.type))
    : []

  return (
    <div className="mt-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
      <div className="text-[11px] text-purple-600 font-medium mb-1">
        {opName.replace(/_/g, ' ')}
      </div>
      {params.prompt && (
        <div className="text-[11px] text-gray-600 mb-1.5 italic line-clamp-3">
          "{params.prompt}"
        </div>
      )}
      {params.combos && (
        <div className="text-[11px] text-gray-600 mb-1.5">
          {params.combos.map((c: any, j: number) => (
            <span key={j}>{j > 0 && ', '}{c.platform_label} {c.label}</span>
          ))}
        </div>
      )}
      {params.formats && (
        <div className="text-[11px] text-gray-600 mb-1.5">
          {params.formats.join(', ')}
        </div>
      )}
      {params.output_languages && (
        <div className="text-[11px] text-gray-600 mb-1.5">
          {params.input_language} → {params.output_languages.join(', ')}
        </div>
      )}
      {excludedAssets.length > 0 && (
        <div className="mb-1.5">
          <div className="text-[11px] text-gray-500 mb-1">
            Excluding {excludedAssets.length} asset{excludedAssets.length !== 1 ? 's' : ''}:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {excludedAssets.map((asset) => (
              <div
                key={asset.id}
                className="flex items-center gap-1 bg-white border border-gray-200 rounded px-1.5 py-0.5"
                title={`${asset.name} (${asset.type})`}
              >
                {(asset.thumbnail_url || asset.image_url) && (
                  <img
                    src={asset.thumbnail_url || asset.image_url}
                    alt=""
                    className="w-5 h-5 object-contain rounded opacity-60"
                  />
                )}
                <span className="text-[10px] text-gray-600 line-through max-w-[120px] truncate">
                  {asset.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <button
        className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-40"
        disabled={isStreaming || isExecuted || isEditorLoading}
        onClick={() => onExecute(toolCall, messageIndex)}
      >
        {isExecuted ? 'Launched' : 'Proceed'}
      </button>
    </div>
  )
}

interface ChatMessageListProps {
  messages: ChatMessage[]
  isStreaming: boolean
  executedActions: Set<number>
  editorIsLoading: boolean
  brandAssets: BrandAsset[]
  onExecuteAction: (toolCall: ToolCall, messageIndex: number) => void
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages, isStreaming, executedActions, editorIsLoading, brandAssets, onExecuteAction,
}) => (
  <>
    {messages.map((msg, i) => (
      <div
        key={i}
        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
            msg.role === 'user'
              ? 'bg-purple-600 text-white whitespace-pre-wrap'
              : 'bg-gray-100 text-gray-900 prose prose-sm overflow-hidden break-words [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_code]:break-words'
          }`}
        >
          {msg.role === 'user' && msg.images && msg.images.length > 0 && (
            <div className="flex gap-1 mb-1 flex-wrap">
              {msg.images.map((img, j) => (
                <img
                  key={j}
                  src={`data:${img.media_type};base64,${img.data}`}
                  alt=""
                  className="w-16 h-16 rounded object-cover"
                />
              ))}
            </div>
          )}
          {msg.role === 'assistant' ? (
            msg.content || msg.toolCall ? (
              <>
                {msg.content && <ReactMarkdown>{msg.content}</ReactMarkdown>}
                {msg.toolCall && (
                  <ToolCallPreview
                    toolCall={msg.toolCall}
                    messageIndex={i}
                    isStreaming={isStreaming}
                    isLastMessage={i === messages.length - 1}
                    isExecuted={executedActions.has(i)}
                    isEditorLoading={editorIsLoading}
                    brandAssets={brandAssets}
                    onExecute={onExecuteAction}
                  />
                )}
              </>
            ) : (
              isStreaming && i === messages.length - 1 ? '...' : ''
            )
          ) : (
            msg.content
          )}
        </div>
      </div>
    ))}
  </>
)

export default ChatMessageList
